import React, { useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, Link2, Mail, UserRound } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { getLandingFormEnvironment, getLandingFormScriptUrl } from '@/utils/landingFormScriptUrl';
import {
  trackLandingContactSubmitted,
  trackLinkedInBookDemoSubmitted,
} from '@/lib/analytics/trackers/landingPage';
import {
  LANDING_LEAD_INTENT_EVENT,
  type LandingLeadIntent,
} from '@/utils/landingLeadIntent';
import styles from './CallToAction.module.css';

type ContactFormState = {
  fullName: string;
  practiceName: string;
  workEmail: string;
  linkedinUrl: string;
  leadIntent: LandingLeadIntent;
};

type ContactFormErrors = Partial<Record<keyof ContactFormState, string>>;

const INITIAL_FORM: ContactFormState = {
  fullName: '',
  practiceName: '',
  workEmail: '',
  linkedinUrl: '',
  leadIntent: 'learn_more',
};

const LEAD_INTENT_LABELS: Record<LandingLeadIntent, string> = {
  book_demo: 'Book a Demo',
  learn_more: 'Learn More',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeLinkedinUrl = (value: string): string => {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const isValidLinkedinUrl = (value: string): boolean => {
  if (!value.trim()) return false;

  try {
    const parsed = new URL(normalizeLinkedinUrl(value));
    return parsed.hostname.toLowerCase().includes('linkedin.com');
  } catch {
    return false;
  }
};

const CallToAction: React.FC = () => {
  const [form, setForm] = useState<ContactFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const handleChange =
    (field: keyof ContactFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleIntentSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, leadIntent: event.target.value as LandingLeadIntent }));
  };

  useEffect(() => {
    const handleLeadIntentChange = (event: Event) => {
      const intent = (event as CustomEvent<{ intent?: LandingLeadIntent }>).detail?.intent;
      if (!intent) return;
      setForm((prev) => ({ ...prev, leadIntent: intent }));
    };

    window.addEventListener(LANDING_LEAD_INTENT_EVENT, handleLeadIntentChange);
    return () => window.removeEventListener(LANDING_LEAD_INTENT_EVENT, handleLeadIntentChange);
  }, []);

  const validateForm = (): ContactFormErrors => {
    const nextErrors: ContactFormErrors = {};
    const trimmedName = form.fullName.trim();
    const trimmedPracticeName = form.practiceName.trim();
    const trimmedEmail = form.workEmail.trim();
    const trimmedLinkedin = form.linkedinUrl.trim();

    if (trimmedName.length < 2) {
      nextErrors.fullName = 'Please enter your full name.';
    }

    if (trimmedPracticeName.length < 2) {
      nextErrors.practiceName = 'Please enter your practice name.';
    }

    if (!emailRegex.test(trimmedEmail)) {
      nextErrors.workEmail = 'Please enter a valid email.';
    }

    if (!isValidLinkedinUrl(trimmedLinkedin)) {
      nextErrors.linkedinUrl = 'Please enter a valid LinkedIn URL.';
    }

    return nextErrors;
  };

  const handleFooterRegisterClick = () => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      nameInputRef.current?.focus();
    }, 150);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      name: form.fullName.trim(),
      practiceName: form.practiceName.trim(),
      email: form.workEmail.trim(),
      linkedin: normalizeLinkedinUrl(form.linkedinUrl),
      leadIntent: form.leadIntent,
      inquiryType: LEAD_INTENT_LABELS[form.leadIntent],
      environment: getLandingFormEnvironment(),
    };

    setIsSubmitting(true);
    try {
      const scriptUrl = getLandingFormScriptUrl();
      const body = new URLSearchParams(payload).toString();
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      trackLandingContactSubmitted({
        has_linkedin: Boolean(payload.linkedin),
        lead_intent: payload.leadIntent,
      });
      if (payload.leadIntent === 'book_demo') {
        trackLinkedInBookDemoSubmitted({
          has_linkedin: Boolean(payload.linkedin),
        });
      }
      showSuccessToast('Thank you!', 'Our team will get in touch with you shortly.');
      setForm(INITIAL_FORM);
      setErrors({});
    } catch (error) {
      showErrorToast(error, 'Submission Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="ready-to-grow-practice" ref={sectionRef} className={styles.section}>
      <div className="landing-container">
        <div className={styles.card}>
          <button
            id="registerInterest"
            type="button"
            className={styles.hiddenTrigger}
            onClick={handleFooterRegisterClick}
            tabIndex={-1}
            aria-hidden
          >
            Jump to contact form
          </button>

          <div className={styles.copy}>
            <h2>Ready to grow your practice?</h2>
            <p>
              Scale your nutrition services with structured workflows, intelligent reporting, and
              systems built for measurable outcomes. Book a demo or ask us to share more details,
              and our team will get in touch with you.
            </p>
          </div>

          <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
            <label className={styles.formField} htmlFor="cta-full-name">
              <span className={styles.formLabel}>Full name</span>
              <span className={`${styles.inputLine} ${errors.fullName ? styles.inputLineError : ''}`}>
                <input
                  id="cta-full-name"
                  ref={nameInputRef}
                  type="text"
                  value={form.fullName}
                  onChange={handleChange('fullName')}
                  placeholder="Enter Your Full Name"
                  className={styles.formInput}
                  autoComplete="name"
                />
                <UserRound className={styles.inputIcon} aria-hidden />
              </span>
              {errors.fullName ? <span className={styles.errorText}>{errors.fullName}</span> : null}
            </label>

            <label className={styles.formField} htmlFor="cta-practice-name">
              <span className={styles.formLabel}>Practice name</span>
              <span className={`${styles.inputLine} ${errors.practiceName ? styles.inputLineError : ''}`}>
                <input
                  id="cta-practice-name"
                  type="text"
                  value={form.practiceName}
                  onChange={handleChange('practiceName')}
                  placeholder="Enter your practice name"
                  className={styles.formInput}
                  autoComplete="organization"
                />
                <Building2 className={styles.inputIcon} aria-hidden />
              </span>
              {errors.practiceName ? (
                <span className={styles.errorText}>{errors.practiceName}</span>
              ) : null}
            </label>

            <label className={styles.formField} htmlFor="cta-email">
              <span className={styles.formLabel}>Email</span>
              <span className={`${styles.inputLine} ${errors.workEmail ? styles.inputLineError : ''}`}>
                <input
                  id="cta-email"
                  type="email"
                  value={form.workEmail}
                  onChange={handleChange('workEmail')}
                  placeholder="Enter your email"
                  className={styles.formInput}
                  autoComplete="email"
                />
                <Mail className={styles.inputIcon} aria-hidden />
              </span>
              {errors.workEmail ? <span className={styles.errorText}>{errors.workEmail}</span> : null}
            </label>

            <label className={styles.formField} htmlFor="cta-linkedin">
              <span className={styles.formLabel}>LinkedIn Profile</span>
              <span className={`${styles.inputLine} ${errors.linkedinUrl ? styles.inputLineError : ''}`}>
                <input
                  id="cta-linkedin"
                  type="url"
                  value={form.linkedinUrl}
                  onChange={handleChange('linkedinUrl')}
                  placeholder="Paste your LinkedIn profile URL"
                  className={styles.formInput}
                  autoComplete="url"
                />
                <Link2 className={styles.inputIcon} aria-hidden />
              </span>
              {errors.linkedinUrl ? (
                <span className={styles.errorText}>{errors.linkedinUrl}</span>
              ) : null}
            </label>

            <label className={styles.formField} htmlFor="cta-lead-intent">
              <span className={styles.formLabel}>I want to</span>
              <span className={styles.selectLine}>
                <select
                  id="cta-lead-intent"
                  name="leadIntent"
                  value={form.leadIntent}
                  onChange={handleIntentSelectChange}
                  className={styles.intentSelect}
                >
                  <option value="book_demo">Book a Demo</option>
                  <option value="learn_more">Learn More</option>
                </select>
                <ChevronDown className={styles.inputIcon} aria-hidden />
              </span>
            </label>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
              data-analytics-event="Landing CTA Clicked"
              data-analytics-id="contact_form_submit"
              data-analytics-label="Contact Form Submit"
              data-analytics-section="ready_to_grow_practice"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;
